import React, { useState, useEffect } from 'react'
import clsx from 'clsx'

import { useRegisterUserMutation } from '../../services/apiSlice'
import Input from '../../components/forms/Input'
import Spinner from '../../components/loading/Spinner'
import ErrorDisplay from './ErrorDisplay'
import {
  containsAnySpecialCharacter,
  errorHandler,
} from '../../services/helpers'
import errorMessages from '../../utils/messages/errorMessages'

const limits = [
  [8, 50],
  [8, 100],
  [8, 100],
  [8, 100],
]

// we need to add all possible routes here like "settings/account" or just block usage of \ or any special character as username
export const frontendRoutes = [
  'landing',
  'home',
  'emailVerification',
  'settings',
  'explore',
  'notifications',
  'messages',
  'bookmarks',
  'communities',
  'games',
  'surprise',
  'media',
]

interface Props {
  userCount: number | 'unknown'
}

const CreateAccount: React.FC<Props> = ({ userCount }) => {
  const [inputErrorFlags, setInputErrorFlags] = useState<number[]>([])
  const [errorMessage, setErrorMessage] = useState('')
  const [registerUser, { isSuccess, isLoading, isError, error }] =
    useRegisterUserMutation()
  const userCountLimit = import.meta.env.VITE_USERCOUNT

  useEffect(() => {
    setInputErrorFlags([])
  }, [])

  useEffect(() => {
    if (isError) {
      const customError = errorHandler(error)
      if (customError && customError.errorCode === 401) {
        setInputErrorFlags([0, 1])
        setErrorMessage(errorMessages['401-b'])
      }
      if (customError && customError.errorCode === 403) {
        setInputErrorFlags([0])
        setErrorMessage(errorMessages['403-b'])
      }
      if (customError && customError.errorCode === 404) {
        setInputErrorFlags([1])
        setErrorMessage(errorMessages['404-b'])
      }
      if (customError && customError.errorCode === 406) {
        setInputErrorFlags([1])
        setErrorMessage(errorMessages['406-b'])
      }
      if (customError && customError.errorCode === 429) {
        setInputErrorFlags([0, 1, 2, 3])
        setErrorMessage(errorMessages['429'])
      }
      if (customError && customError.errorCode === 500) {
        setInputErrorFlags([0, 1, 2, 3])
        setErrorMessage(errorMessages['500-b'])
      }
      if (customError && customError.errorCode === 501) {
        setInputErrorFlags([0, 1, 2, 3])
        setErrorMessage(errorMessages['501-b'])
      }
    }

    if (isSuccess) {
      setInputErrorFlags([])
      setErrorMessage('')
    }
  }, [isSuccess, isError, error])

  const handleAccountCreation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      setInputErrorFlags([])
      const formData = new FormData(e.currentTarget)
      const username = formData.get('username') as string
      const email = formData.get('email') as string
      const password = formData.get('password') as string
      const confirmPassword = formData.get('confirm password') as string
      const dob = formData.get('date of birth') as string

      if (frontendRoutes.includes(username)) {
        setInputErrorFlags([0])
        setErrorMessage(errorMessages.INVALID_USERNAME)
        return
      }

      // space is also a special character
      if (containsAnySpecialCharacter(username)) {
        setInputErrorFlags([0])
        setErrorMessage(errorMessages.SPECIAL_CHARACTER)
        return
      }

      let formDataLength = 0
      const blanks: string[] = []
      const notWithinLimits: string[] = []
      const errorFlags: number[] = []
      const errorMessagesArray: string[] = []
      for (const pair of formData.entries()) {
        if (pair[1] === '') {
          blanks.push(pair[0])
          errorFlags.push(formDataLength)
        }

        const strLength = (pair[1] as string).length

        if (formDataLength === 4) {
          break
        }

        if (
          strLength !== 0 &&
          (strLength < limits[formDataLength][0] ||
            strLength > limits[formDataLength][1])
        ) {
          notWithinLimits.push(pair[0])
          errorFlags.push(formDataLength)
        }

        formDataLength += 1
      }

      setInputErrorFlags(errorFlags)

      if (blanks.length > 0) {
        errorMessagesArray.push(
          `Please do not leave blank the ( ${blanks.join(', ')} ) fields.`,
        )
      }

      if (notWithinLimits.length > 0) {
        errorMessagesArray.push(
          `These fields ( ${notWithinLimits.join(', ')} ) are not within the required character limit count.`,
        )
      }

      if (password !== confirmPassword) {
        errorFlags.push(2)
        errorFlags.push(3)
        errorMessagesArray.push(
          'Please make sure both password fields are of equal value.',
        )
      }

      if (errorMessagesArray.length > 0) {
        setErrorMessage(`${errorMessagesArray.join(' - ')}`)
        return
      }

      registerUser({
        username: username,
        email,
        password,
        dateOfBirth: dob,
      })
    } catch (err) {
      setErrorMessage(errorMessages['500-b'])
    }
  }

  return (
    <div className="flex h-full w-[650px] flex-col items-center justify-center">
      <form
        onSubmit={handleAccountCreation}
        autoComplete="off"
        className="border-theme landing-dynamic-width flex h-[500px] flex-col items-center justify-center"
      >
        <div className="mb-[20px] text-4xl font-bold">Create your account</div>
        <Input
          display={'Username'}
          type={'text'}
          name={'username'}
          min={8}
          max={50}
          showLimit={true}
          inputErrorFlag={inputErrorFlags.includes(0)}
        />
        <Input
          display={'Email'}
          type={'email'}
          name={'email'}
          min={8}
          max={100}
          showLimit={false}
          inputErrorFlag={inputErrorFlags.includes(1)}
        />
        <Input
          display={'Password'}
          type={'password'}
          name={'password'}
          min={8}
          max={100}
          showLimit={false}
          inputErrorFlag={inputErrorFlags.includes(2)}
        />
        <Input
          display={'Confirm Password'}
          type={'password'}
          name={'confirm password'}
          min={8}
          max={100}
          showLimit={false}
          inputErrorFlag={inputErrorFlags.includes(3)}
        />
        <div
          className={clsx('w-full text-base font-bold', {
            'text-red': inputErrorFlags.includes(4),
          })}
        >
          <h1 className="pb-[5px]">Date of Birth:</h1>
        </div>
        <div className="w-full text-base font-bold">
          <input type="date" name="date of birth" />
        </div>

        <div className="border-theme text-sub mt-[20px] flex w-full justify-evenly rounded-[5px] border py-[5px] pl-[10px] font-bold italic">
          <div className="flex grow">
            <p>user count</p>
          </div>
          <div className="flex grow">current: {userCount}</div>
          <div className="flex grow">max: {userCountLimit}</div>
        </div>

        <div className="text-sub mt-[15px]">
          <p>
            If you are using Internet Explorer 11 or below, please switch to
            another browser (preferably google chrome) to avoid compatibility
            issues.
          </p>
        </div>
        <div className="mt-[15px] flex w-full flex-col">
          <button
            className="bgd-theme-wb text-main-invert h-[40px] w-full cursor-pointer rounded-full bg-red-900 text-lg font-medium"
            type="submit"
          >
            Submit
          </button>
        </div>
        <div className="mt-[15px] flex w-full">
          <ErrorDisplay errorMessage={errorMessage} />
        </div>

        {isSuccess && (
          <div className="mt-[15px] w-full text-green-500">
            <h1>Account successfully created.</h1>
          </div>
        )}

        {isLoading && (
          <div className="mt-[20px] flex w-full items-center justify-center">
            <Spinner />
          </div>
        )}
      </form>
    </div>
  )
}

export default CreateAccount
